import type { AdLanguageBase } from './ad-language';
import type { LandingCopy, LandingImmersiveSeedText } from './landing-page-api';

/**
 * The copy a new landing page starts with, per base language (`adLanguageBase`): the same placeholder as the English
 * seed (`DEFAULT_LANDING_COPY` and the English immersive seed, both in landing-page-api.ts) written in each language,
 * so a page opens in its website's language before Lumio writes its real copy. Same meaning as the English seed, no
 * claim of its own, the figures and the "18+" kept.
 *
 * The lines are AD CONTENT in the page's language, not Studio UI copy: they never go through i18n (the owner's Studio
 * language must not change what the page says). Spanish is neutral Latin American, Portuguese Brazilian, French
 * Canadian (as the mini-game lines, mini-game-copy.ts).
 */

/** The base languages other than English (English is the document seed itself). */
export type TranslatedSeedLanguage = Exclude<AdLanguageBase, 'en'>;

/** The immersive (full-screen game) fields a page copy seed leaves empty: `withImmersiveSeeds` fills them. */
const NO_IMMERSIVE: Pick<
  LandingCopy,
  'hint' | 'disclaimer' | 'prizes' | 'decoys' | 'modal_title' | 'modal_text' | 'hub'
> = { hint: '', disclaimer: '', prizes: [], decoys: [], modal_title: '', modal_text: '', hub: '' };

/** The page copy seed per language (English: `DEFAULT_LANDING_COPY`). No terms of its own (see that seed). */
export const LANDING_SEED_COPY: Readonly<Record<TranslatedSeedLanguage, LandingCopy>> = {
  es: {
    badge: 'Bono de bienvenida',
    eyebrow: 'Hasta',
    headline: '100% de bono',
    subline: '+ 100 giros gratis',
    cta: 'Reclama tu bono',
    micro: '18+ · Aplican TyC',
    steps_title: 'Cómo funciona',
    steps: ['Crea tu cuenta en menos de un minuto', 'Haz tu primer depósito', 'Tu bono llega a tu cuenta'],
    terms_title: 'Términos y condiciones',
    terms_html: '',
    key_terms: '18+ · Solo jugadores nuevos · Aplican TyC',
    usps: [
      { title: 'Registro rápido', text: 'Únete en pocos pasos' },
      { title: 'Pagos locales', text: 'Paga con los métodos que ya usas' },
      { title: 'Soporte amable', text: 'Ayuda cuando la necesites' },
      { title: 'Juega donde quieras', text: 'En tu celular o tu computadora' },
    ],
    games_title: 'Juegos populares',
    faq_title: 'Preguntas',
    faq: [
      {
        q: '¿Cómo reclamo el bono?',
        a: 'Crea tu cuenta, haz tu primer depósito y el bono llega a tu cuenta.',
      },
      { q: '¿Hay requisitos de apuesta?', a: 'Los términos del bono en nuestro sitio web explican cómo funciona.' },
      { q: '¿Qué métodos de pago puedo usar?', a: 'Todos los métodos de pago que aparecen en nuestro sitio web.' },
      { q: '¿Quién puede reclamar esta oferta?', a: 'Jugadores nuevos de 18 años o más.' },
    ],
    closing: 'Tu bono te espera',
    ...NO_IMMERSIVE,
  },
  pt: {
    badge: 'Bônus de boas-vindas',
    eyebrow: 'Até',
    headline: '100% de bônus',
    subline: '+ 100 rodadas grátis',
    cta: 'Resgate seu bônus',
    micro: '18+ · Termos se aplicam',
    steps_title: 'Como funciona',
    steps: ['Crie sua conta em menos de um minuto', 'Faça seu primeiro depósito', 'Seu bônus cai na sua conta'],
    terms_title: 'Termos e condições',
    terms_html: '',
    key_terms: '18+ · Apenas novos jogadores · Termos se aplicam',
    usps: [
      { title: 'Cadastro rápido', text: 'Entre em poucos passos' },
      { title: 'Pagamentos locais', text: 'Pague com os métodos que você já usa' },
      { title: 'Suporte atencioso', text: 'Ajuda quando você precisar' },
      { title: 'Jogue onde quiser', text: 'No celular ou no computador' },
    ],
    games_title: 'Jogos populares',
    faq_title: 'Perguntas',
    faq: [
      {
        q: 'Como resgato o bônus?',
        a: 'Crie sua conta, faça seu primeiro depósito e o bônus cai na sua conta.',
      },
      { q: 'Há requisitos de apostas?', a: 'Os termos do bônus no nosso site explicam como o bônus funciona.' },
      { q: 'Quais métodos de pagamento posso usar?', a: 'Todos os métodos de pagamento mostrados no nosso site.' },
      { q: 'Quem pode resgatar esta oferta?', a: 'Novos jogadores com 18 anos ou mais.' },
    ],
    closing: 'Seu bônus está esperando',
    ...NO_IMMERSIVE,
  },
  fr: {
    badge: 'Bonus de bienvenue',
    eyebrow: "Jusqu'à",
    headline: '100% de bonus',
    subline: '+ 100 tours gratuits',
    cta: 'Obtenez votre bonus',
    micro: '18+ · Conditions applicables',
    steps_title: 'Comment ça marche',
    steps: [
      "Créez votre compte en moins d'une minute",
      'Faites votre premier dépôt',
      'Votre bonus arrive dans votre compte',
    ],
    terms_title: 'Conditions générales',
    terms_html: '',
    key_terms: '18+ · Nouveaux joueurs uniquement · Conditions applicables',
    usps: [
      { title: 'Inscription rapide', text: 'Rejoignez-nous en quelques étapes' },
      { title: 'Paiements locaux', text: 'Payez avec les moyens que vous utilisez déjà' },
      { title: 'Soutien amical', text: "De l'aide quand vous en avez besoin" },
      { title: 'Jouez partout', text: 'Sur votre téléphone ou votre ordinateur' },
    ],
    games_title: 'Jeux populaires',
    faq_title: 'Questions',
    faq: [
      {
        q: 'Comment obtenir le bonus?',
        a: 'Créez votre compte, faites votre premier dépôt et le bonus arrive dans votre compte.',
      },
      {
        q: 'Y a-t-il des exigences de mise?',
        a: 'Les conditions du bonus sur notre site web expliquent comment il fonctionne.',
      },
      { q: 'Quels modes de paiement puis-je utiliser?', a: 'Tous les modes de paiement affichés sur notre site web.' },
      { q: 'Qui peut profiter de cette offre?', a: 'Les nouveaux joueurs de 18 ans ou plus.' },
    ],
    closing: 'Votre bonus vous attend',
    ...NO_IMMERSIVE,
  },
  af: {
    badge: 'Welkomsbonus',
    eyebrow: 'Tot',
    headline: '100% bonus',
    subline: '+ 100 gratis draaie',
    cta: 'Eis jou bonus',
    micro: '18+ · Bepalings geld',
    steps_title: 'Hoe dit werk',
    steps: ["Skep jou rekening in minder as 'n minuut", 'Maak jou eerste deposito', 'Jou bonus beland in jou rekening'],
    terms_title: 'Bepalings en voorwaardes',
    terms_html: '',
    key_terms: '18+ · Slegs nuwe spelers · Bepalings geld',
    usps: [
      { title: 'Vinnige registrasie', text: "Sluit in 'n paar stappe aan" },
      { title: 'Plaaslike betalings', text: 'Betaal met die metodes wat jy reeds gebruik' },
      { title: 'Vriendelike hulp', text: 'Hulp wanneer jy dit nodig het' },
      { title: 'Speel oral', text: 'Op jou foon of jou rekenaar' },
    ],
    games_title: 'Gewilde speletjies',
    faq_title: 'Vrae',
    faq: [
      {
        q: 'Hoe eis ek die bonus?',
        a: 'Skep jou rekening, maak jou eerste deposito en die bonus beland in jou rekening.',
      },
      { q: 'Is daar insetvereistes?', a: 'Die bonusbepalings op ons webwerf verduidelik hoe die bonus werk.' },
      { q: 'Watter betaalmetodes kan ek gebruik?', a: 'Elke betaalmetode wat op ons webwerf gewys word.' },
      { q: 'Wie kan hierdie aanbod eis?', a: 'Nuwe spelers van 18 jaar of ouer.' },
    ],
    closing: 'Jou bonus wag vir jou',
    ...NO_IMMERSIVE,
  },
  ro: {
    badge: 'Bonus de bun venit',
    eyebrow: 'Până la',
    headline: '100% bonus',
    subline: '+ 100 rotiri gratuite',
    cta: 'Ia-ți bonusul',
    micro: '18+ · Se aplică T&C',
    steps_title: 'Cum funcționează',
    steps: ['Creează-ți contul în mai puțin de un minut', 'Fă primul depozit', 'Bonusul ajunge în contul tău'],
    terms_title: 'Termeni și condiții',
    terms_html: '',
    key_terms: '18+ · Doar jucători noi · Se aplică T&C',
    usps: [
      { title: 'Înregistrare rapidă', text: 'Te alături în câțiva pași' },
      { title: 'Plăți locale', text: 'Plătești cu metodele pe care le folosești deja' },
      { title: 'Suport prietenos', text: 'Ajutor când ai nevoie' },
      { title: 'Joacă oriunde', text: 'Pe telefon sau pe calculator' },
    ],
    games_title: 'Jocuri populare',
    faq_title: 'Întrebări',
    faq: [
      {
        q: 'Cum revendic bonusul?',
        a: 'Creează-ți contul, fă primul depozit și bonusul ajunge în contul tău.',
      },
      { q: 'Există cerințe de rulaj?', a: 'Termenii bonusului de pe site-ul nostru explică cum funcționează bonusul.' },
      { q: 'Ce metode de plată pot folosi?', a: 'Orice metodă de plată afișată pe site-ul nostru.' },
      { q: 'Cine poate revendica această ofertă?', a: 'Jucătorii noi cu vârsta de 18 ani sau peste.' },
    ],
    closing: 'Bonusul tău te așteaptă',
    ...NO_IMMERSIVE,
  },
  hu: {
    badge: 'Üdvözlő bónusz',
    eyebrow: 'Akár',
    headline: '100% bónusz',
    subline: '+ 100 ingyenes pörgetés',
    cta: 'Kérd a bónuszod',
    micro: '18+ · ÁSZF érvényes',
    steps_title: 'Így működik',
    steps: ['Hozd létre a fiókod egy percen belül', 'Tedd meg az első befizetésed', 'A bónusz megérkezik a fiókodba'],
    terms_title: 'Általános szerződési feltételek',
    terms_html: '',
    key_terms: '18+ · Csak új játékosoknak · ÁSZF érvényes',
    usps: [
      { title: 'Gyors regisztráció', text: 'Csatlakozz néhány lépésben' },
      { title: 'Helyi fizetési módok', text: 'Fizess a már megszokott módokon' },
      { title: 'Barátságos ügyfélszolgálat', text: 'Segítség, amikor szükséged van rá' },
      { title: 'Játssz bárhol', text: 'Telefonon vagy számítógépen' },
    ],
    games_title: 'Népszerű játékok',
    faq_title: 'Kérdések',
    faq: [
      {
        q: 'Hogyan igényelhetem a bónuszt?',
        a: 'Hozd létre a fiókod, tedd meg az első befizetésed, és a bónusz megérkezik a fiókodba.',
      },
      {
        q: 'Vannak megforgatási követelmények?',
        a: 'A weboldalunkon található bónuszfeltételek leírják, hogyan működik a bónusz.',
      },
      { q: 'Milyen fizetési módokat használhatok?', a: 'A weboldalunkon feltüntetett bármelyik fizetési módot.' },
      { q: 'Ki igényelheti ezt az ajánlatot?', a: 'A 18. életévüket betöltött új játékosok.' },
    ],
    closing: 'Vár a bónuszod',
    ...NO_IMMERSIVE,
  },
};

/** The immersive (full-screen game) seed per language (English: `LANDING_IMMERSIVE_SEED_EN`): the call to play per
 *  game, the small print, the win modal, and the words of the reveal and its decoys. */
export const LANDING_IMMERSIVE_SEEDS: Readonly<Record<TranslatedSeedLanguage, LandingImmersiveSeedText>> = {
  es: {
    hints: { wheel: '¡Gira la rueda y reclama tu bono!', pick: '¡Elige un premio y reclama tu bono!' },
    hint_any: '¡Juega y reclama tu bono!',
    disclaimer: '18+ solo jugadores nuevos. Aplican TyC. El juego es ilustrativo.',
    modal_title: '¡Felicidades!',
    modal_text: 'Tu bono de bienvenida te espera',
    free_spins: 'giros gratis',
    spins: 'giros',
    bonus: 'bono de bienvenida',
    cashback: '20% de cashback',
  },
  pt: {
    hints: { wheel: 'Gire a roda e resgate seu bônus!', pick: 'Escolha um prêmio e resgate seu bônus!' },
    hint_any: 'Jogue e resgate seu bônus!',
    disclaimer: '18+ apenas novos jogadores. Termos se aplicam. O jogo é ilustrativo.',
    modal_title: 'Parabéns!',
    modal_text: 'Seu bônus de boas-vindas está esperando',
    free_spins: 'rodadas grátis',
    spins: 'rodadas',
    bonus: 'bônus de boas-vindas',
    cashback: '20% de cashback',
  },
  fr: {
    hints: { wheel: 'Tournez la roue et obtenez votre bonus!', pick: 'Choisissez un prix et obtenez votre bonus!' },
    hint_any: 'Jouez et obtenez votre bonus!',
    disclaimer: '18+ nouveaux joueurs uniquement. Conditions applicables. Le jeu est illustratif.',
    modal_title: 'Félicitations!',
    modal_text: 'Votre bonus de bienvenue vous attend',
    free_spins: 'tours gratuits',
    spins: 'tours',
    bonus: 'bonus de bienvenue',
    cashback: '20% de cashback',
  },
  af: {
    hints: { wheel: 'Draai die wiel en eis jou bonus!', pick: "Kies 'n prys en eis jou bonus!" },
    hint_any: 'Speel en eis jou bonus!',
    disclaimer: '18+ slegs nuwe spelers. Bepalings geld. Die speletjie is illustratief.',
    modal_title: 'Baie geluk!',
    modal_text: 'Jou welkomsbonus wag vir jou',
    free_spins: 'gratis draaie',
    spins: 'draaie',
    bonus: 'welkomsbonus',
    cashback: '20% cashback',
  },
  ro: {
    hints: { wheel: 'Învârte roata și ia-ți bonusul!', pick: 'Alege un premiu și ia-ți bonusul!' },
    hint_any: 'Joacă și ia-ți bonusul!',
    disclaimer: '18+ doar jucători noi. Se aplică T&C. Jocul este ilustrativ.',
    modal_title: 'Felicitări!',
    modal_text: 'Bonusul tău de bun venit te așteaptă',
    free_spins: 'rotiri gratuite',
    spins: 'rotiri',
    bonus: 'bonus de bun venit',
    cashback: '20% cashback',
  },
  hu: {
    hints: { wheel: 'Pörgesd meg a kereket, és kérd a bónuszod!', pick: 'Válassz egy nyereményt, és kérd a bónuszod!' },
    hint_any: 'Játssz, és kérd a bónuszod!',
    disclaimer: '18+ csak új játékosoknak. ÁSZF érvényes. A játék illusztráció.',
    modal_title: 'Gratulálunk!',
    modal_text: 'Vár az üdvözlő bónuszod',
    free_spins: 'ingyenes pörgetés',
    spins: 'pörgetés',
    bonus: 'üdvözlő bónusz',
    cashback: '20% cashback',
  },
};
